<template>
  <AuthLayout :mode="mode">
    <Section
      heading="Manage Members"
      description="<a href='/members/add-member' class='text-primary-100 font-semibold underline'>Add new member</a>"
      :mode="mode"
    />
    <AppCard :mode="mode">
      <template #body>
        <div
          class="mb-6 flex items-center justify-between gap-3 sm:flex-col sm:gap-4"
        >
          <div class="flex-1 sm:w-full">
            <div class="relative flex items-center">
              <Search
                :size="20"
                :stroke-width="1.75"
                class="absolute left-4 opacity-40"
              />
              <AppInput
                :mode="mode"
                type="search"
                placeholder="Search by company"
                v-model="memberSearch.company_name"
                classes="!pl-11"
                :hasLabel="false"
              />
            </div>
          </div>
          <div class="w-[200px] sm:w-full">
            <AppButton title="Search" @action="search" />
          </div>
        </div>
        <template v-if="paginatedItems.length > 0">
          <div class="mb-4 hidden xl:block">
            <AppSelect
              :mode="mode"
              title="Sort by"
              :modelValue="mobileSort"
              @update:modelValue="applyMobileSort"
            >
              <template #options>
                <option value="">Default (best match)</option>
                <option
                  v-for="option in sortableColumns"
                  :key="`${option.sortKey}:asc`"
                  :value="`${option.sortKey}:asc`"
                >
                  {{ option.name }} (A-Z)
                </option>
                <option
                  v-for="option in sortableColumns"
                  :key="`${option.sortKey}:desc`"
                  :value="`${option.sortKey}:desc`"
                >
                  {{ option.name }} (Z-A)
                </option>
              </template>
            </AppSelect>
          </div>
          <AppTable
            :mode="mode"
            :columns="columns"
            :sortKey="sort.key"
            :sortOrder="sort.order"
            @sort="toggleSort"
          >
            <template #rows>
              <tr v-for="(member, key) in paginatedItems" :key="key">
                <td>
                  <RouterLink
                    v-if="member._source.member.company_name"
                    :to="{
                      name: 'member-details',
                      params: { id: member._source.member.member_id },
                    }"
                    v-tooltip="`${member._source.member.company_name}`"
                  >
                    <span class="block max-w-[320px] truncate">{{
                      member._source.member.company_name
                    }}</span>
                  </RouterLink>
                  <span v-else>-</span>
                </td>
                <td>
                  <FallbackText
                    v-if="member._source.member.company_phone1"
                    :value="member._source.member.company_phone1"
                  />
                  <FallbackText
                    v-else
                    :value="member._source.member.company_phone2"
                  />
                </td>
                <td>
                  <FallbackText :value="member._source.member.company_email" />
                </td>
                <td>
                  <a
                    :href="
                      member._source.member.url.includes('http')
                        ? member._source.member.url
                        : 'https://' + member._source.member.url
                    "
                    target="_blank"
                    v-if="member._source.member.url"
                    class="text-primary-100"
                  >
                    <span
                      class="block max-w-[120px] truncate"
                      v-tooltip="`${member._source.member.url}`"
                      >{{ member._source.member.url }}</span
                    >
                  </a>
                  <span v-else>-</span>
                </td>
                <td>
                  <div class="flex items-center gap-2">
                    <TableAction
                      :mode="mode"
                      @itemAction="
                        $redirectTo(
                          'member-details',
                          member._source.member.member_id,
                        )
                      "
                      action="view"
                    />
                    <TableAction
                      :mode="mode"
                      @itemAction="
                        $redirectTo(
                          'member-edit',
                          member._source.member.member_id,
                        )
                      "
                      action="edit"
                    />
                    <TableAction
                      :mode="mode"
                      :isActive="
                        member._source.member.member_id !==
                        loggedInUser.member_id
                      "
                      @itemAction="
                        confirmDelete(member._source.member.member_id)
                      "
                      action="delete"
                    />
                  </div>
                </td>
              </tr>
            </template>
          </AppTable>
          <MobileTable
            :mode="mode"
            :fields="{
              company: 'Company',
              phone: 'Phone',
              email: 'Email',
              url: 'URL',
              actions: 'Actions',
            }"
            :data="paginatedItems"
          >
            <template #default="{ row, fieldKey }">
              <template v-if="fieldKey === 'company'">
                <RouterLink
                  v-if="row._source.member.company_name"
                  :to="{
                    name: 'member-details',
                    params: { id: row._source.member.member_id },
                  }"
                  v-tooltip="`${row._source.member.company_name}`"
                >
                  <span class="block max-w-[120px] truncate">{{
                    row._source.member.company_name
                  }}</span>
                </RouterLink>
                <span v-else>-</span>
              </template>
              <template v-else-if="fieldKey === 'phone'">
                <FallbackText
                  v-if="row._source.member.company_phone1"
                  :value="row._source.member.company_phone1"
                  :isTruncate="false"
                />
                <FallbackText
                  v-else
                  :value="row._source.member.company_phone2"
                  :isTruncate="false"
                />
              </template>
              <template v-else-if="fieldKey === 'email'">
                <FallbackText
                  :value="row._source.member.company_email"
                  :isTruncate="false"
                />
              </template>
              <template v-else-if="fieldKey === 'url'">
                <a
                  :href="
                    row._source.member.url.includes('http')
                      ? row._source.member.url
                      : 'https://' + row._source.member.url
                  "
                  target="_blank"
                  v-if="row._source.member.url"
                  class="text-primary-100"
                >
                  <span
                    class="block max-w-[120px] truncate"
                    v-tooltip="`${row._source.member.url}`"
                    >{{ row._source.member.url }}</span
                  >
                </a>
                <span v-else>-</span>
              </template>
              <template v-else-if="fieldKey === 'actions'">
                <div class="flex items-center gap-2">
                  <TableAction
                    :mode="mode"
                    @itemAction="
                      $redirectTo(
                        'member-details',
                        row._source.member.member_id,
                      )
                    "
                    action="view"
                  />
                  <TableAction
                    :mode="mode"
                    @itemAction="
                      $redirectTo('member-edit', row._source.member.member_id)
                    "
                    action="edit"
                  />
                  <TableAction
                    :mode="mode"
                    :isActive="
                      row._source.member.member_id !== loggedInUser.member_id
                    "
                    @itemAction="confirmDelete(row._source.member.member_id)"
                    action="delete"
                  />
                </div>
              </template>
            </template>
          </MobileTable>
        </template>
        <EmptyData
          v-else
          :notFound="'No members represented!'"
          :isClear="true"
          @clear="clear"
          :mode="mode"
        />
        <AppPagination
          :key="`${sort.key}:${sort.order}`"
          :mode="mode"
          :data="sortedItems"
          @pageChanged="updatePaginatedItems"
        />
      </template>
    </AppCard>
    <DeleteRecord
      :currentId="currentMemberId"
      :deleteType="'member'"
      :mode="mode"
      @recordDeleted="recordDeleted"
      @cancelDelete="currentMemberId = null"
    />
  </AuthLayout>
</template>

<script>
import { Search } from "lucide-vue-next";
import Section from "@C/Common/Section.vue";
import ApiRequest from "@L/ApiRequest";
import tableSort from "@L/tableSort";
import { useGlobalErrorHandler } from "@L/errorHandler";
import { useUserStore } from "@S/user";
import { useToastStore } from "@S/toast";
import { useLoaderStore } from "@S/loader";
import EmptyData from "@C/Common/EmptyData.vue";
import AppCard from "@C/Common/AppCard.vue";
import AppTable from "@C/Tables/AppTable.vue";
import AppPagination from "@C/Common/AppPagination.vue";
import AppInput from "@C/Form/AppInput.vue";
import AppSelect from "@C/Form/AppSelect.vue";
import DeleteRecord from "@C/DeleteRecord.vue";
import AuthLayout from "@A/AuthLayout.vue";
import FallbackText from "@C/Tables/FallbackText.vue";
import MobileTable from "@C/Tables/MobileTable.vue";
import AppButton from "@C/Common/AppButton.vue";
import TableAction from "@C/Tables/TableAction.vue";

export default {
  name: "ManageMembers",
  mixins: [tableSort],
  components: {
    AuthLayout,
    Section,
    EmptyData,
    AppCard,
    AppTable,
    Search,
    AppPagination,
    TableAction,
    AppInput,
    AppSelect,
    DeleteRecord,
    FallbackText,
    MobileTable,
    AppButton,
  },
  setup() {
    const userStore = useUserStore();
    const toastStore = useToastStore();
    const loaderStore = useLoaderStore();
    const { handleError } = useGlobalErrorHandler();
    return {
      userStore,
      toastStore,
      loaderStore,
      handleError,
    };
  },
  props: {
    mode: String,
  },
  data() {
    return {
      members: [],
      memberSearch: {
        company_name: "",
      },
      currentMemberId: null,
      columns: [
        {
          name: "Company",
          sortKey: "company_name",
          classes: "min-w-[320px]",
        },
        {
          name: "Phone",
          sortKey: "phone",
        },
        {
          name: "Email",
          sortKey: "company_email",
        },
        {
          name: "URL",
          sortKey: "url",
        },
        {
          name: "Actions",
          classes: "w-[100px]",
        },
      ],
      paginatedItems: [],
    };
  },
  created() {
    if (!this.isAdmin) {
      this.toastStore.show(
        "You do not have permission to see this page",
        "error",
      );
      this.$router.push({ name: "home" });
      return;
    }
  },
  methods: {
    async search() {
      this.loaderStore.setIsLoading(true);
      const payload = this.memberSearch;
      try {
        const { data } = await ApiRequest().post(`/member_search`, payload);
        if (data) {
          this.members = data;
        } else {
          this.members = [];
        }
      } catch (e) {
        this.handleError(e);
      } finally {
        this.loaderStore.setIsLoading(false);
      }
    },
    clear() {
      this.memberSearch = {
        company_name: "",
      };
    },
    confirmDelete(id) {
      this.currentMemberId = id;
    },
    recordDeleted() {
      this.search();
      this.currentMemberId = null;
      this.toastStore.show("Member deleted successfully!", "success");
    },
    updatePaginatedItems(paginatedData) {
      this.paginatedItems = paginatedData;
    },
    sortValue(member, key) {
      const source = member._source.member;
      switch (key) {
        case "company_name":
          return source.company_name;
        case "phone":
          // Match the column, which falls back to the second number.
          return source.company_phone1 || source.company_phone2;
        case "company_email":
          return source.company_email;
        case "url":
          return source.url;
        default:
          return "";
      }
    },
  },
  computed: {
    sortSource() {
      return this.members;
    },
    loggedInUser() {
      return this.userStore.user.member;
    },
    isAdmin() {
      return this.userStore.user.global_admin;
    },
  },
};
</script>
